import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Clock, Copy, Check, ExternalLink, Loader2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useFormatters } from '@/lib/hooks/useFormatters'
import type { CheckoutOutcome } from '@/lib/checkout/outcome'

interface PurchaseOutcomeViewProps {
  outcome: CheckoutOutcome
  /** Back to the form after a refusal. */
  onRetry: () => void
  /** Shown once the payment is confirmed; omitted, there is no button. */
  onDone?: () => void
  doneLabel?: string
  /** Replaces the free claim's confirmation text (a company purchase zeroed by a coupon is not "you are in"). */
  freeText?: { title: string; description: string }
  className?: string
}

/** A value to copy, with a button that says it worked. */
export const CopyField = ({ value, label }: { value: string; label: string }) => {
  const { t } = useTranslation('checkout')
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      /* Clipboard blocked (insecure context, permissions): the text is
         selectable, so the buyer can still copy it by hand. */
    }
  }

  return (
    <div>
      <p className="mb-1 text-xs text-gray-500">{label}</p>
      <div className="flex min-w-0 items-stretch gap-2">
        <code className="min-w-0 flex-1 select-all break-all rounded-md border border-gray-200 bg-white p-2 font-mono text-xs text-gray-800">
          {value}
        </code>
        <Button type="button" variant="outline" size="sm" onClick={copy} className="h-auto flex-shrink-0">
          {copied ? <Check className="mr-1 h-4 w-4 text-green-600" /> : <Copy className="mr-1 h-4 w-4" />}
          {copied ? t('purchase.copied') : t('purchase.copy')}
        </Button>
      </div>
    </div>
  )
}

/**
 * What happened after submitting: the PIX to pay, the boleto to print, or
 * the card's answer. A pending PIX or boleto keeps a "waiting" line, because
 * the purchase flow keeps checking and this view flips to "paid" by itself.
 */
export function PurchaseOutcomeView({ outcome, onRetry, onDone, doneLabel, freeText, className }: PurchaseOutcomeViewProps) {
  const { t } = useTranslation(['checkout', 'products'])
  const { formatDateTime } = useFormatters()

  const waiting = (
    <p className="flex items-center justify-center gap-2 text-sm text-gray-600">
      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      {t('purchase.outcome.waiting')}
    </p>
  )

  if (outcome.kind === 'pix') {
    return (
      <div className={cn('space-y-4', className)}>
        <div className="text-center">
          <h3 className="font-semibold text-gray-900">{t('purchase.outcome.pixTitle')}</h3>
          <p className="mt-1 text-sm text-gray-600">{t('purchase.outcome.pixDescription')}</p>
        </div>
        {outcome.qrCodeUrl && (
          <img
            src={outcome.qrCodeUrl}
            alt={t('purchase.outcome.pixQrAlt')}
            className="mx-auto h-48 w-48 rounded-lg border border-gray-200 bg-white p-2"
          />
        )}
        <CopyField value={outcome.pixCode} label={t('purchase.outcome.pixCopyLabel')} />
        {outcome.expiresAt && (
          <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
            <Clock className="h-3.5 w-3.5" />
            {t('purchase.outcome.pixExpires', { date: formatDateTime(outcome.expiresAt) })}
          </p>
        )}
        {waiting}
      </div>
    )
  }

  if (outcome.kind === 'boleto') {
    return (
      <div className={cn('space-y-4', className)}>
        <div className="text-center">
          <h3 className="font-semibold text-gray-900">{t('purchase.outcome.boletoTitle')}</h3>
          <p className="mt-1 text-sm text-gray-600">{t('purchase.outcome.boletoDescription')}</p>
        </div>
        {outcome.boletoUrl && (
          <Button asChild variant="secondary" className="w-full">
            <a href={outcome.boletoUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              {t('purchase.outcome.openBoleto')}
            </a>
          </Button>
        )}
        {outcome.barcode && <CopyField value={outcome.barcode} label={t('purchase.outcome.boletoBarcode')} />}
        {waiting}
      </div>
    )
  }

  if (outcome.kind === 'paid') {
    return (
      <div className={cn('space-y-4 text-center', className)}>
        <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
        <div>
          <h3 className="font-semibold text-gray-900">{t('purchase.outcome.paidTitle')}</h3>
          <p className="mt-1 text-sm text-gray-600">{t('purchase.outcome.paidDescription')}</p>
        </div>
        {onDone && (
          <Button type="button" variant="secondary" className="w-full" onClick={onDone}>
            {doneLabel || t('purchase.outcome.continue')}
          </Button>
        )}
      </div>
    )
  }

  if (outcome.kind === 'free') {
    return (
      <div className={cn('space-y-4 text-center', className)} data-testid="free-claimed">
        <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
        <div>
          <h3 className="font-semibold text-gray-900">{freeText?.title ?? t('products:freeClaim.doneTitle')}</h3>
          <p className="mt-1 text-sm text-gray-600">{freeText?.description ?? t('products:freeClaim.doneDescription')}</p>
        </div>
        {onDone && (
          <Button type="button" variant="secondary" className="w-full" onClick={onDone}>
            {doneLabel || t('purchase.outcome.continue')}
          </Button>
        )}
      </div>
    )
  }

  if (outcome.kind === 'processing') {
    return (
      <div className={cn('space-y-4 text-center', className)}>
        <Clock className="mx-auto h-12 w-12 text-amber-500" />
        <div>
          <h3 className="font-semibold text-gray-900">{t('purchase.outcome.processingTitle')}</h3>
          <p className="mt-1 text-sm text-gray-600">{t('purchase.outcome.processingDescription')}</p>
        </div>
        {waiting}
      </div>
    )
  }

  return (
    <div className={cn('space-y-4 text-center', className)}>
      <XCircle className="mx-auto h-12 w-12 text-red-500" />
      <div>
        <h3 className="font-semibold text-gray-900">{t('purchase.outcome.refusedTitle')}</h3>
        <p className="mt-1 text-sm text-gray-600">{t('purchase.outcome.refusedDescription')}</p>
      </div>
      <Button type="button" variant="outline" className="w-full" onClick={onRetry}>
        {t('purchase.tryAgain')}
      </Button>
    </div>
  )
}
